// src/ctxReflector/components/Instances/views/Licensed/LicensedPage.tsx
import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";

// Tabla genérica
import { GenericTable } from "@ui-components/Table/GenericTable";

// Botones, inputs, modales, alertas
import GenericButton from "@ui-components/Button/GenericButton";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import { GenericFilter } from "@ui-components/Filter/GenericFilter";
import GenericAlert from "@ui-components/Alert/GenericAlert";

// Iconos
import { ReactComponent as IconEdit } from "@assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "@assets/Icons/Icon_Delete.svg";

// Hook del overlay
import { useProcessingOverlay } from "@hooks/useProcessingOverlay";

// Hook de Axios con Auth0
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";

// Interfaz + columnas
import { LicensedInstance, columns as baseColumns } from "./LicensedData";

// Requests (ya actualizados)
import {
    fetchLicensedInstances as getAllInstances,
    fetchLicensedInstanceById as getInstanceById,
    updateLicensedInstance as updateInstance,
    deleteLicensedInstance as removeLicense,
} from "./request/request";

function LicensedPage() {
    // Con esto todavía verificas si el user está logueado o si la app está cargando.
    // Lo puedes seguir usando para condicionar la UI si gustas.
    const { isAuthenticated, isLoading } = useAuth0();

    // <-- Aquí obtenemos la instancia de Axios con token "automático".
    const axiosInstance = useAxiosAuth0();

    // Lista de instancias
    const [instances, setInstances] = useState<LicensedInstance[]>([]);
    const [isFetching, setIsFetching] = useState(false);

    // Para el buscador
    const [searchTerm, setSearchTerm] = useState("");
    const [searchColumn, setSearchColumn] = useState("All");
    const searchOptions = [
        "All",
        "Name",
        "Description",
        "License",
        "ID",
        "High Availability",
    ];

    // Edit modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editInstance, setEditInstance] = useState<LicensedInstance | null>(null);
    const [originalDescription, setOriginalDescription] = useState("");
    const [newDescription, setNewDescription] = useState("");

    // Delete confirm
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
    const [instanceToDelete, setInstanceToDelete] = useState<LicensedInstance | null>(null);

    // Alerta final
    const [isResultAlertOpen, setIsResultAlertOpen] = useState(false);
    const [alertVariant, setAlertVariant] = useState<"success" | "error">("success");
    const [alertTitle, setAlertTitle] = useState("");
    const [alertDescription, setAlertDescription] = useState("");

    // Hook para spinner/overlay
    const overlay = useProcessingOverlay(isFetching);

    // 1) Cargar la lista cuando el usuario está logueado y la app no está en loading
    useEffect(() => {
        if (isLoading || !isAuthenticated) return;
        loadInstances();
    }, [isLoading, isAuthenticated]);

    const loadInstances = async () => {
        try {
            setIsFetching(true);
            const resp = await getAllInstances(axiosInstance);
            setInstances(resp.data || []);
        } catch (error: unknown) {
            console.error("Error fetching Instances =>", error);
        } finally {
            setIsFetching(false);
        }
    };

    const handleRefresh = () => {
        loadInstances();
    };

    // Filtro para la tabla
    const filteredInstances = instances.filter((row) => {
        const term = searchTerm.toLowerCase().trim();
        if (!term) return true;
        const includes = (val: string) => val.toLowerCase().includes(term);
        const ha = row.cluster ? row.cluster.name : "Off";

        switch (searchColumn) {
            case "Name":
                return includes(row.name);
            case "Description":
                return includes(row.description);
            case "License":
                return includes(row.license);
            case "ID":
                return includes(row.id);
            case "High Availability":
                return includes(ha);
            case "All":
            default:
                return (
                    includes(row.name) ||
                    includes(row.description) ||
                    includes(row.license) ||
                    includes(row.id) ||
                    includes(ha)
                );
        }
    });

    // 2) Editar una instancia
    const handleEditClick = async (rowData: LicensedInstance) => {
        try {
            setIsFetching(true);
            const resp = await getInstanceById(axiosInstance, rowData.id);
            const freshData: LicensedInstance = resp.data;

            setEditInstance(freshData);
            setOriginalDescription(freshData.description || "");
            setNewDescription(freshData.description || "");
            setIsModalOpen(true);
        } catch (error: unknown) {
            console.error("Error fetching instance =>", error);
        } finally {
            setIsFetching(false);
        }
    };

    const handleSaveEdit = async () => {
        if (!editInstance) return;
        try {
            setIsFetching(true);

            await updateInstance(axiosInstance, editInstance.id, {
                description: newDescription,
            });

            await loadInstances();

            setAlertVariant("success");
            setAlertTitle("Instance updated");
            setAlertDescription(
                `La instancia "${editInstance.name}" fue actualizada correctamente.`
            );
        } catch (err: unknown) {
            console.error("Error updating instance =>", err);
            setAlertVariant("error");
            setAlertTitle("Update error");
            setAlertDescription("No se pudo actualizar la instancia, intente de nuevo.");
        } finally {
            setIsFetching(false);
            setIsModalOpen(false);
            setIsResultAlertOpen(true);
        }
    };

    const isDisabled = newDescription === originalDescription;

    // 3) Eliminar una instancia
    const handleDeleteClick = (rowData: LicensedInstance) => {
        setInstanceToDelete(rowData);
        setDeleteConfirmInput("");
        setIsDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!instanceToDelete) return;
        if (deleteConfirmInput.trim().toLowerCase() !== "confirm") {
            setIsDeleteConfirmOpen(false);
            showLicenseSafe();
            return;
        }

        setIsDeleteConfirmOpen(false);
        try {
            setIsFetching(true);
            await removeLicense(axiosInstance, instanceToDelete.id);

            setInstances((prev) =>
                prev.filter((inst) => inst.id !== instanceToDelete.id)
            );

            setAlertVariant("success");
            setAlertTitle("Deleted!");
            setAlertDescription(
                `La instancia "${instanceToDelete.name}" se ha eliminado.`
            );
        } catch (err: unknown) {
            console.error("Error removing license =>", err);
            setAlertVariant("error");
            setAlertTitle("Error");
            setAlertDescription("No se pudo eliminar la instancia, intente de nuevo.");
        } finally {
            setIsFetching(false);
            setInstanceToDelete(null);
            setIsResultAlertOpen(true);
        }
    };

    const showLicenseSafe = () => {
        setAlertVariant("success");
        setAlertTitle("Your License is safe!");
        setAlertDescription("No se realizó ningún cambio.");
        setIsResultAlertOpen(true);
    };

    // Columnas: agregamos el Edit y Delete como antes
    const finalColumns = baseColumns.map((col) => {
        if (col.header === "Actions" && col.renderCell) {
            return {
                ...col,
                renderCell: (row: LicensedInstance) => (
                    <div className="flex items-center gap-2">
                        <button
                            className="
                                border border-semantic-border-secondary
                                rounded
                                bg-semantic-bg-primary
                                text-semantic-fg-secondary
                                hover:bg-semantic-state-active
                                w-10 h-10 flex items-center justify-center
                            "
                            onClick={() => handleEditClick(row)}
                        >
                            <IconEdit className="w-6 h-6" />
                        </button>

                        <button
                            className="
                                border border-semantic-border-secondary
                                rounded
                                bg-semantic-bg-danger-secondary
                                text-semantic-fg-danger
                                hover:bg-red-100
                                w-10 h-10 flex items-center justify-center
                            "
                            onClick={() => handleDeleteClick(row)}
                        >
                            <IconDelete className="w-5 h-5" />
                        </button>
                    </div>
                ),
            };
        }
        return col;
    });

    // Render final
    return (
        <div className="relative p-4">

            {/* Filter + Refresh en la misma fila */}
            <div className="flex items-center justify-end mb-4 gap-2">
                <GenericFilter
                    searchTerm={searchTerm}
                    onSearchTermChange={setSearchTerm}
                    searchColumn={searchColumn}
                    onSearchColumnChange={setSearchColumn}
                    columnOptions={searchOptions}
                />
                <GenericButton
                    label="Refresh"
                    variant="secondary"
                    onClick={handleRefresh}
                />
            </div>

            {/* Tabla */}
            <GenericTable
                columns={finalColumns}
                data={filteredInstances}
                initialRowsPerPage={5}
                rowsPerPageOptions={[5, 10, 20]}
                width="100%"
                tableClassName="bg-semantic-bg-primary"
            />

            {/* Modal Edit */}
            <GenericModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="Edit Instance"
                size="md"
            >
                <div className="mb-4">
                    <label className="text-sm font-medium text-semantic-text-primary">
                        Description
                    </label>
                    <GenericInput
                        placeholder="Instance description"
                        value={newDescription}
                        onChange={setNewDescription}
                    />
                </div>

                <div className="flex justify-end space-x-2">
                    <button
                        className="
                            px-4 py-2
                            text-sm
                            font-medium
                            text-semantic-text-primary
                            bg-semantic-bg-primary
                            border border-semantic-border-secondary
                            rounded
                            hover:bg-semantic-state-active
                            transition-colors
                        "
                        onClick={() => setIsModalOpen(false)}
                    >
                        Cancel
                    </button>
                    <button
                        className={`
                            px-4 py-2
                            text-sm
                            font-medium
                            text-white
                            bg-semantic-text-brand
                            rounded
                            transition-colors
                            ${!isDisabled
                                ? "hover:bg-opacity-90 cursor-pointer"
                                : "opacity-50 cursor-not-allowed"
                            }
                        `}
                        onClick={handleSaveEdit}
                        disabled={isDisabled}
                    >
                        Save
                    </button>
                </div>
            </GenericModal>

            {/* ALERT: Delete confirm */}
            <GenericAlert
                isOpen={isDeleteConfirmOpen}
                onClose={() => setIsDeleteConfirmOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Are you sure?"
                description="Once deleted, you will not be able to recover this License! Enter 'confirm' to continue..."
                cancelLabel="Cancel"
                confirmLabel="Yes, delete it!"
                variant="warning"
                confirmButtonDisabled={
                    deleteConfirmInput.trim().toLowerCase() !== "confirm"
                }
            >
                <div className="mt-4">
                    <GenericInput
                        placeholder="confirm"
                        value={deleteConfirmInput}
                        onChange={setDeleteConfirmInput}
                    />
                </div>
            </GenericAlert>

            {/* ALERT: Resultado */}
            <GenericAlert
                isOpen={isResultAlertOpen}
                onClose={() => setIsResultAlertOpen(false)}
                onConfirm={() => setIsResultAlertOpen(false)}
                title={alertTitle}
                description={alertDescription}
                cancelLabel="Close"
                confirmLabel="OK"
                variant={alertVariant}
            />

            {/* Overlay spinner */}
            {overlay}
        </div>
    );
}

export default LicensedPage;
